import { useEffect, useState } from "react";
import { Eye, Bell, ShieldCheck, History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateShort } from "@/lib/api";
import { SectionTitle, Pill, EmptyState } from "@/components/Bits";
import ShareClientPortal from "@/components/ShareClientPortal";

const SECTION_LABELS = {
  overview: "Project overview",
  timeline: "Timeline and milestones",
  activity: "Client-facing activity updates",
  presentations: "Presentations and designs",
  selections: "Product selections",
  decisions: "Approval requests and decisions",
  documents: "Shared documents",
  invoices: "Invoices and payment history",
  budget_summary: "Project budget summaries",
};

const NOTIFY_OPTIONS = [
  ["every", "Every verified sign-in"],
  ["daily", "First visit per client each day"],
  ["first", "First-ever visit only"],
  ["off", "Notifications disabled"],
];

const STATUS_TONE = {
  Active: "bg-[#BEC221] text-ink",
  Invited: "bg-[#EDEFC4] text-ink",
  "Not Shared": "bg-line text-stone",
  "Access Disabled": "bg-ink text-white",
};

const Card = ({ icon: Icon, title, children, testId }) => (
  <section className="card-surface p-7" data-testid={testId}>
    <div className="mb-6 flex items-center gap-3">
      <Icon className="h-4 w-4 text-lime-dark" strokeWidth={1.5} />
      <h3 className="font-serif text-xl text-ink">{title}</h3>
    </div>
    {children}
  </section>
);

export default function ClientPortalTab({ projectId, onPortalToggle }) {
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [previewing, setPreviewing] = useState(false);

  const load = () => {
    api.get(`/projects/${projectId}/client-portal`).then((r) => setData(r.data)).catch(() => setData(null));
    api.get(`/projects/${projectId}/portal-access-log`).then((r) => setLog(r.data)).catch(() => setLog([]));
  };

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (body) => {
    try {
      const { data: fresh } = await api.patch(`/projects/${projectId}/client-portal`, body);
      setData(fresh);
      if (body.client_portal_enabled !== undefined) onPortalToggle?.(body.client_portal_enabled);
      toast.success("Client portal updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const preview = async () => {
    setPreviewing(true);
    const tab = window.open("", "_blank");
    try {
      const { data: session } = await api.post(`/projects/${projectId}/client-preview`);
      if (tab) tab.location.href = `${window.location.origin}/client-preview/${session.token}`;
      else toast.error("Allow pop-ups to open the client preview");
    } catch (e) {
      tab?.close();
      toast.error(apiError(e));
    } finally {
      setPreviewing(false);
    }
  };

  if (!data) return <div className="py-16 text-center text-sm text-fog">Loading client portal…</div>;

  const s = data.summary;

  return (
    <div className="space-y-8" data-testid="tab-client-portal">
      <Card icon={ShieldCheck} title="Portal Overview" testId="portal-overview-card">
        <div className="flex flex-wrap items-center gap-4">
          <Pill tone={STATUS_TONE[data.status] || "bg-line text-stone"} testId="portal-status-pill">
            {data.status}
          </Pill>
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              data-testid="portal-enabled-toggle"
              type="checkbox"
              checked={data.portal_enabled}
              onChange={(e) => patch({ client_portal_enabled: e.target.checked })}
              className="h-4 w-4 accent-[#BEC221]"
            />
            Portal access enabled
          </label>
        </div>
        <div className="mt-7 grid grid-cols-2 gap-6 border-t border-line pt-6 sm:grid-cols-4" data-testid="portal-summary">
          {[
            ["Authorised contacts", s.contacts],
            ["Verified contacts", s.verified_contacts],
            ["Viewing sessions", s.sessions],
            ["Last client visit", s.last_access_at ? dateShort(s.last_access_at) : "Never"],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="num text-2xl text-ink">{value}</div>
              <div className="overline mt-1 leading-tight">{label}</div>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs text-fog">All times shown in UTC.</p>
      </Card>

      <Card icon={Eye} title="Preview as Client" testId="portal-preview-card">
        <p className="max-w-2xl text-sm leading-relaxed text-stone">
          Opens this project exactly as your client sees it, in a new tab. The preview is read-only —
          nothing can be approved, paid or sent, and it never counts as a client visit.
        </p>
        <button
          data-testid="preview-as-client-btn"
          onClick={preview}
          disabled={previewing}
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-ink px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-ink/85 disabled:opacity-50"
        >
          {previewing ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
          Preview as Client
        </button>
      </Card>

      <div className="card-surface p-7" data-testid="portal-share-card">
        <ShareClientPortal
          projectId={projectId}
          portalEnabled={data.portal_enabled}
          onToggle={() => patch({ client_portal_enabled: true })}
          onChanged={load}
        />
      </div>

      <Card icon={ShieldCheck} title="Client Visibility" testId="portal-visibility-card">
        <p className="max-w-2xl text-sm leading-relaxed text-stone">
          Switch off anything you'd rather keep internal for now. These rules are enforced in the
          data itself, so hidden sections never reach the portal, the API or a download. Internal
          notes, costs, vendors and margins are always private.
        </p>
        <button
          data-testid="visibility-preview-btn"
          onClick={preview}
          disabled={previewing}
          className="mt-5 inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink transition-colors hover:border-lime disabled:opacity-50"
        >
          <Eye className="h-4 w-4" strokeWidth={1.5} /> Preview Client Dashboard
        </button>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {data.sections.map((key) => (
            <label key={key} className="flex items-center gap-3 text-sm text-ink">
              <input
                data-testid={`visibility-${key}`}
                type="checkbox"
                checked={data.client_visibility[key] !== false}
                onChange={(e) => patch({ client_visibility: { [key]: e.target.checked } })}
                className="h-4 w-4 accent-[#BEC221]"
              />
              {SECTION_LABELS[key] || key}
            </label>
          ))}
        </div>
      </Card>

      <Card icon={Bell} title="Notification Settings" testId="portal-notifications-card">
        <p className="max-w-2xl text-sm leading-relaxed text-stone">
          Choose how often you hear about verified client visits to this project. Notices arrive by
          email{data.notify_email ? ` at ${data.notify_email}` : ""} and in your Updates panel.
        </p>
        <div className="mt-6 space-y-3">
          {NOTIFY_OPTIONS.map(([value, label]) => (
            <label key={value} className="flex items-center gap-3 text-sm text-ink">
              <input
                data-testid={`notify-mode-${value}`}
                type="radio"
                name="portal-notify-mode"
                checked={data.portal_notify_mode === value}
                onChange={() => patch({ portal_notify_mode: value })}
                className="h-4 w-4 accent-[#BEC221]"
              />
              {label}
            </label>
          ))}
        </div>
      </Card>

      <Card icon={History} title="Access Activity" testId="portal-access-log-card">
        {log.length === 0 ? (
          <EmptyState testId="portal-access-log-empty" title="No portal activity yet"
            body="Invitations, verification codes and verified sign-ins will appear here." />
        ) : (
          <div className="-mx-7 divide-y divide-line border-t border-line">
            {log.map((e) => (
              <div key={e.id} data-testid={`portal-access-row-${e.id}`}
                className="flex flex-wrap items-center gap-4 px-7 py-4">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-ink">{e.name || e.email}</div>
                  <div className="mt-1 break-all text-xs text-fog">{e.email}</div>
                </div>
                <span className="text-xs uppercase tracking-[0.15em] text-stone">{e.event_label}</span>
                {e.invite_status && <Pill tone="bg-line text-stone">{e.invite_status}</Pill>}
                <span className="num shrink-0 text-xs text-fog">
                  {new Date(e.created_at).toLocaleString("en-US", { timeZone: "UTC", dateStyle: "medium", timeStyle: "short" })} UTC
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
